import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import type { ActionProposal, DeepLink, TableBlock } from '../../lib/assistantApi'

/** Allow http(s) image sources in assistant markdown (data: URIs rendered separately). */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'img'],
  attributes: {
    ...defaultSchema.attributes,
    img: ['src', 'alt', 'title', 'width', 'height', 'className'],
  },
  protocols: {
    ...defaultSchema.protocols,
    src: [...(defaultSchema.protocols?.src ?? []), 'http', 'https'],
  },
}

const markdownComponents: Components = {
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent underline underline-offset-2 break-words [overflow-wrap:anywhere]"
    >
      {children}
    </a>
  ),
  img: ({ src, alt }) =>
    src ? (
      <img
        src={src}
        alt={alt || 'Generated image'}
        className="mt-2 mb-2 max-h-64 w-full rounded-xl border border-border object-contain bg-surface"
        loading="lazy"
      />
    ) : null,
  code: ({ className, children }) => {
    const isBlock = Boolean(className)
    if (isBlock) {
      return (
        <code className={`block font-mono text-xs ${className ?? ''}`}>{children}</code>
      )
    }
    return (
      <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">{children}</code>
    )
  },
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-lg border border-border bg-surface p-2 last:mb-0">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-border pl-3 text-foreground-muted last:mb-0">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-border bg-surface px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-2 py-1 align-top">{children}</td>
  ),
}

const IMAGE_URL_RE =
  /(https?:\/\/(?:images\.unsplash\.com|picsum\.photos)\/[^\s)]+|https?:\/\/[^\s)]+?\.(?:png|jpe?g|gif|webp)(?:\?[^\s)]*)?|https?:\/\/[^\s)]+?\/(?:uploads|api\/media\/files)\/[^\s)]+|data:image\/[a-zA-Z+]+;base64,[A-Za-z0-9+/=]+)/gi

/** Collect unique image URLs (https + data URI) from assistant message text. */
export function extractImageUrls(content: string): string[] {
  if (!content) return []
  const found: string[] = []
  const seen = new Set<string>()
  for (const m of content.matchAll(/!\[[^\]]*]\(([^)]+)\)/g)) {
    const url = m[1]?.trim()
    if (url && !seen.has(url)) {
      seen.add(url)
      found.push(url)
    }
  }
  for (const m of content.matchAll(IMAGE_URL_RE)) {
    const url = (m[0] || '').trim()
    if (url && !seen.has(url)) {
      seen.add(url)
      found.push(url)
    }
  }
  return found
}

/** Promote bare https image URLs into markdown images; strip data URIs (rendered as <img>). */
export function promoteImageUrlsInMarkdown(content: string): string {
  if (!content) return content
  let out = content.replace(/data:image\/[a-zA-Z+]+;base64,[A-Za-z0-9+/=]+/gi, '').trim()
  out = out.replace(IMAGE_URL_RE, (url, _g, offset, full) => {
    if (url.startsWith('data:')) return ''
    const before = full.slice(Math.max(0, offset - 3), offset)
    if (before.endsWith('](')) return url
    return `\n\n![Generated image](${url})\n\n`
  })
  return out
}

/** One button per route — multiple shift tools often all emit "Open shifts". */
export function uniqueDeepLinks(links?: DeepLink[] | null): DeepLink[] {
  if (!links?.length) return []
  const seen = new Set<string>()
  const out: DeepLink[] = []
  for (const link of links) {
    const route = link?.route?.trim()
    if (!route || seen.has(route)) continue
    seen.add(route)
    out.push(link)
  }
  return out
}

/** Sentinel id when Confirm all / Reject all is in flight (not a real proposal). */
export const CONFIRM_ALL_PROGRESS_ID = '__confirm_all__'

export interface AssistantMessageContentProps {
  role: 'user' | 'assistant' | 'system'
  content: string
  proposals?: ActionProposal[]
  deepLinks?: DeepLink[]
  tables?: TableBlock[]
  busy?: boolean
  /** Proposal currently being confirmed/rejected (shows progress on that card). */
  confirmingProposalId?: string | null
  confirmProgressLabel?: string | null
  onConfirm?: (proposal: ActionProposal, confirm: boolean) => void
  /** Confirm or reject every proposal on this message (shown when 2+ pending). */
  onConfirmAll?: (proposals: ActionProposal[], confirm: boolean) => void
  onDeepLink?: (route: string) => void
}

export default function AssistantMessageContent({
  role,
  content,
  proposals,
  deepLinks,
  tables,
  busy = false,
  confirmingProposalId = null,
  confirmProgressLabel = null,
  onConfirm,
  onConfirmAll,
  onDeepLink,
}: AssistantMessageContentProps) {
  if (role === 'user') {
    return <span className="whitespace-pre-wrap">{content}</span>
  }

  const images = extractImageUrls(content)
  const markdown = promoteImageUrlsInMarkdown(content)
  // Avoid duplicate https imgs: markdown renders those; only mount data: URIs here.
  const dataImages = images.filter((u) => u.startsWith('data:image/'))

  return (
    <>
      <div className="assistant-markdown text-sm leading-relaxed">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
          components={markdownComponents}
        >
          {markdown}
        </ReactMarkdown>
      </div>
      {tables?.map((table, idx) => (
        <div
          key={`${table.reportId ?? table.title}-${idx}`}
          className="mt-2 mb-2 overflow-x-auto rounded-xl border border-border"
        >
          {table.title ? (
            <div className="border-b border-border bg-surface px-2 py-1.5 text-xs font-semibold">
              {table.title}
              {table.rows?.length != null ? (
                <span className="ml-2 font-normal text-foreground-muted">
                  ({table.rows.length} row{table.rows.length === 1 ? '' : 's'})
                </span>
              ) : null}
            </div>
          ) : null}
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr>
                {(table.columns ?? []).map((col) => (
                  <th
                    key={col}
                    className="border border-border bg-surface px-2 py-1 text-left font-semibold whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(table.rows ?? []).slice(0, 100).map((row, rIdx) => (
                <tr key={rIdx}>
                  {(row ?? []).map((cell, cIdx) => (
                    <td key={cIdx} className="border border-border px-2 py-1 align-top max-w-[16rem] truncate">
                      {cell == null ? '' : String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {table.reportId ? (
            <button
              type="button"
              className="block w-full px-2 py-1.5 text-left text-xs text-accent underline"
              onClick={() => onDeepLink?.('/reporting')}
            >
              Open in Reporting
            </button>
          ) : null}
        </div>
      ))}
      {dataImages.map((src) => (
        <img
          key={src.slice(0, 64)}
          src={src}
          alt="Generated image"
          className="mt-2 mb-2 max-h-64 w-full rounded-xl border border-border object-contain bg-surface"
          loading="lazy"
        />
      ))}
      {uniqueDeepLinks(deepLinks).map((link) => (
        <button
          key={link.route}
          type="button"
          className="mt-2 block text-xs text-accent underline"
          onClick={() => onDeepLink?.(link.route)}
        >
          {link.label}
        </button>
      ))}
      {proposals?.map((p) => {
        const previewUrl =
          typeof p.args?.imageUrl === 'string' && p.args.imageUrl.trim()
            ? p.args.imageUrl.trim()
            : null
        const batchBusy = confirmingProposalId === CONFIRM_ALL_PROGRESS_ID
        const isConfirming = confirmingProposalId === p.id || batchBusy
        const confirmLabel = confirmingProposalId === p.id
          ? p.toolName === 'proposePublishMonthSchedule'
            ? 'Publishing…'
            : p.toolName === 'proposeReassignShifts' ||
                p.toolName === 'proposeRedistributeHoursTowardNorm'
              ? 'Updating…'
              : 'Working…'
          : batchBusy
            ? '…'
            : 'Confirm'
        return (
          <div
            key={p.id}
            className="mt-2 space-y-2 rounded-xl border border-border bg-surface p-2"
            data-testid={isConfirming && !batchBusy ? 'assistant-proposal-confirming' : undefined}
          >
            <p className="text-xs font-medium">{p.summary}</p>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Proposed image preview"
                data-testid="assistant-proposal-image"
                className="max-h-56 w-full rounded-lg border border-border object-contain bg-background"
                loading="lazy"
              />
            ) : null}
            {confirmingProposalId === p.id && confirmProgressLabel ? (
              <p
                className="text-xs text-foreground-muted"
                data-testid="assistant-confirm-progress"
                aria-live="polite"
              >
                {confirmProgressLabel}
              </p>
            ) : null}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="assistant-confirm"
                disabled={busy}
                onClick={() => onConfirm?.(p, true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1 text-xs text-white disabled:opacity-70"
              >
                {confirmingProposalId === p.id ? (
                  <span
                    className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden
                  />
                ) : null}
                {confirmLabel}
              </button>
              <button
                type="button"
                data-testid="assistant-reject"
                disabled={busy}
                onClick={() => onConfirm?.(p, false)}
                className="rounded-lg border border-border px-2.5 py-1 text-xs disabled:opacity-70"
              >
                Reject
              </button>
            </div>
          </div>
        )
      })}
      {/* After cards so auto-scroll-to-bottom lands on Confirm all (mobile drawer). */}
      {proposals && proposals.length >= 2 && onConfirmAll ? (
        <div
          className="sticky bottom-0 z-10 mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-2 py-2 shadow-sm"
          data-testid="assistant-confirm-all-bar"
        >
          <span className="text-xs text-foreground-muted">
            {proposals.length} pending actions
          </span>
          {confirmingProposalId === CONFIRM_ALL_PROGRESS_ID && confirmProgressLabel ? (
            <p
              className="w-full text-xs text-foreground-muted"
              data-testid="assistant-confirm-all-progress"
              aria-live="polite"
            >
              {confirmProgressLabel}
            </p>
          ) : null}
          <button
            type="button"
            data-testid="assistant-confirm-all"
            disabled={busy}
            onClick={() => onConfirmAll(proposals, true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1 text-xs text-white disabled:opacity-70"
          >
            {confirmingProposalId === CONFIRM_ALL_PROGRESS_ID ? (
              <span
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden
              />
            ) : null}
            Confirm all
          </button>
          <button
            type="button"
            data-testid="assistant-reject-all"
            disabled={busy}
            onClick={() => onConfirmAll(proposals, false)}
            className="rounded-lg border border-border px-2.5 py-1 text-xs disabled:opacity-70"
          >
            Reject all
          </button>
        </div>
      ) : null}
    </>
  )
}
